import { Component, ElementRef, EventEmitter, Input, Output, SimpleChanges, ViewChild } from "@angular/core";
import { VideoStreamService } from "@app/directives/_veri/veriVideo/v-stream-collection/stream.service";
import { FlvPlayerMediaInfo, FlvPlayer, FlvPlayerStatisticsInfo } from "@app/interfaces/IStreamPlayer";
import { IVideoStreamError } from "@app/interfaces/IVideoStreamError";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import './flv-player.component.scss';

declare const flvjs;

@UntilDestroy()
@Component({
    selector: 'v-flv-player',
    template: require('./flv-player.component.html')
})
export class VFlvPlayer {
    @Input() sourceUrl: string;
    @Input() hasAudio: boolean;
    @Input() streamTimeout: number;
    @Input() timeoutOnMouseIdle: boolean;

    @Output() error = new EventEmitter<{ type: string, message: string, details: IVideoStreamError }>();
    @Output() receivedMedia = new EventEmitter<{ mediaInfo: FlvPlayerMediaInfo }>();
    @Output() streamTimeEnded = new EventEmitter();
    @Output() videoPlaybackNotSupported = new EventEmitter();
    @Output() receivedMetaData = new EventEmitter<{ metadata: any }>();
    @Output() receivedScriptData = new EventEmitter<{ data: any }>();
    @Output() statisics = new EventEmitter<{ statisticsInfo: FlvPlayerStatisticsInfo }>();
    @Output() ready = new EventEmitter();
    @Output() seeked = new EventEmitter();
    @Output() resumed = new EventEmitter();
    @Output() paused = new EventEmitter();
    @Output() closed = new EventEmitter();

    @ViewChild('video') htmlVideoElement: ElementRef;

    player: FlvPlayer = null;
    previousSourceUrl = "";

    displayTimer: number;

    streamTimeoutTimer: ReturnType<typeof setTimeout>;
    streamTimeoutInterval: ReturnType<typeof setInterval>;

    constructor(
        private readonly streamService: VideoStreamService) { }

    ngOnInit(): void {
        this.setupListener();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['sourceUrl']) {
            this.updatePlayer();
        }
    }

    ngAfterViewInit(): void {
        this.updatePlayer();
    }

    ngOnDestroy(): void {
        this.destroyPlayer();
        this.unloadHtmlElement();
        this.removeHtmlElement();
        this.removeTimers();
    }


    //--

    private removeTimers(): void {
        clearTimeout(this.streamTimeoutTimer);
        this.streamTimeoutTimer = null;
    }

    private updatePlayer(): void {
        this.player = this.setupPlayer();
    }

    private setupPlayer(): FlvPlayer {

        this.destroyPlayer();

        const featureList = flvjs.getFeatureList();

        if (!featureList.mseFlvPlayback || !featureList.mseLiveFlvPlayback) {
            this.videoPlaybackNotSupported.emit();
            return null;
        }

        if (!this.sourceUrl) {
            return null;
        }

        const player = this.loadMedia();
        this.setupEvents(player);

        return player;
    }

    private destroyPlayer(): void {
        if (!this.player) {
            this.unloadHtmlElement();
            return;
        }

        try {
            this.player.pause();
            this.player.unload();
            this.player.detachMediaElement();
            this.player.destroy();
        }
        catch (ex) {
            console.warn("Error at FlvPlayer.destroyPlayer", ex);
        }


        this.player = null;
    }

    private unloadHtmlElement(): void {
        if (!this.htmlVideoElement) {
            return;
        }

        this.htmlVideoElement.nativeElement.pause();
        this.htmlVideoElement.nativeElement.removeAttribute('src');
        this.htmlVideoElement.nativeElement.load();
    }

    private removeHtmlElement(): void {
        this.htmlVideoElement.nativeElement.remove();
    }

    private loadMedia(): FlvPlayer {
        if (!this.htmlVideoElement) {
            return;
        }

        const mediaSource = {
            type: "flv",
            isLive: true,
            hasAudio: this.hasAudio,
            hasVideo: true,
            withCredentials: false,
            url: this.sourceUrl
        };
        const player = flvjs.createPlayer(mediaSource, {
            enableWorker: true,
            lazyLoadMaxDuration: 3 * 60,
            seekType: "range",
        });

        player.attachMediaElement(this.htmlVideoElement.nativeElement);
        player.load();
        
        flvjs.LoggingControl.enableAll = false;

        return player;
    }

    private setupEvents(player: FlvPlayer): void {
        if (!player) {
            return;
        }

        player.on(flvjs.Events.ERROR, (type: string, message: string, details: IVideoStreamError) => {
            this.error.emit({ type, message, details });
        });

        player.on(flvjs.Events.MEDIA_INFO, (mediaInfo: FlvPlayerMediaInfo) => {
            this.receivedMedia.emit({ mediaInfo });

            this.startTimer();
        });

        player.on(flvjs.Events.META_DATA_ARRIVED, (metadata: any) => {
            this.receivedMetaData.emit({ metadata });
        });

        player.on(flvjs.Events.SCRIPTDATA_ARRIVED, (data: any) => {
            this.receivedScriptData.emit({ data });
        });

        player.on(flvjs.Events.STATISTICS_INFO, (statisticsInfo: FlvPlayerStatisticsInfo) => {
            this.statisics.emit({ statisticsInfo });
        });

        player.on(flvjs.Events.READY, () => {
            this.ready.emit();
        });

        player.on(flvjs.Events.SEEKED, () => {
            this.seeked.emit();
        });

        player.on(flvjs.Events.RESUMED, () => {
            this.startTimer();
            this.resumed.emit();
        });

        player.on(flvjs.Events.PAUSED, () => {
            clearInterval(this.streamTimeoutInterval);
            this.paused.emit();
        });

        player.on(flvjs.Events.CLOSED, () => {
            this.closed.emit();
        });
    }

    private startTimer(): void {
        if (this.timeoutOnMouseIdle) {
            this.startMouseIdleTimer();
            return;
        }

        if (!this.streamTimeout || this.streamTimeout <= 0) {
            this.streamTimeout = 5;
        }

        clearTimeout(this.streamTimeoutTimer);

        this.streamTimeoutTimer = setTimeout(() => {

            try {
                if (this.player) {
                    this.player.pause();
                    this.player.unload();
                }
            }
            catch (x) {
            }

            this.streamTimeEnded.emit({ player: this.player });

        }, this.streamTimeout * 60000);
    }

    private startMouseIdleTimer(): void {
        clearTimeout(this.streamTimeoutTimer);
        clearInterval(this.streamTimeoutInterval);

        this.streamTimeoutTimer = setTimeout(() => {

            clearInterval(this.streamTimeoutInterval);
            this.displayTimer = 0;

            try {
                if (this.player) {
                    this.player.pause();
                    this.player.unload();
                }
            }
            catch (x) {
            }

            this.streamTimeEnded.emit({ player: this.player });

        }, 30_000);

        let timer = 29;

        this.streamTimeoutInterval = setInterval(() => {
            this.displayTimer = timer;
            timer--;
        }, 1000)


    }


    private resetMouseIdleTimeout(): void {
        if (!this.timeoutOnMouseIdle || this.htmlVideoElement?.nativeElement?.paused) {
            clearInterval(this.streamTimeoutInterval);
            return;
        }

        this.displayTimer = 30;
        this.startMouseIdleTimer();
    }


    private setupListener(): void {
        this.streamService.streamResetTrigger.pipe(untilDestroyed(this)).subscribe(() => {
            this.resetMouseIdleTimeout();
        })
    }
}
